import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUserFriends, faFlag } from '@fortawesome/free-solid-svg-icons';
import EventDetailsSkeletonLoader from './EventDetailsSkeletonLoader';
import CustomModal from './CustomModal';
import LevelAndUserButton from './LevelAndUserButton';

function EventDetails() {
  const { eventId } = useParams();
  const { user } = useUser();
  const [event, setEvent] = useState(null);
  const [isPlayer, setIsPlayer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  // Holds modal title, message, and confirm handler
  const [modalContent, setModalContent] = useState({ title: '', message: '', onConfirm: null });
  const navigate = useNavigate();

  useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  useEffect(() => {
    const startTime = Date.now();
    setLoading(true);

    fetch(`/server/crazy-cup-server/getEventDetails.php?id=${eventId}`)
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          setEvent(data);
          if (user) {
            const username = user.username;
            setIsPlayer(data.players.includes(username));
          }
        } else {
          console.error('Error: Event data is null or undefined.');
        }
      })
      .catch((error) => console.error('Error fetching event details:', error))
      .finally(() => {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(200 - elapsedTime, 0);
        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      });
  }, [eventId, user]);

  const handleStartEvent = () => {
  if (!event) return;

  const currentDate = new Date();
  const eventDate = new Date(event.date);

  if (currentDate < eventDate) {
    setModalContent({
      title: "Event Not Started",
      message: "This event cannot be started until the scheduled date.",
      onConfirm: () => setModalOpen(false),
    });
    setModalOpen(true);
    return;
  }

  if (isPlayer) {
    setModalContent({
      title: "Start Event",
      message: `Make your way to ${event.courses[0]?.name}.`,
      onConfirm: () => {
        setModalOpen(false);
        navigate(`/event/${eventId}/enter-scores`);
      },
    });
    setModalOpen(true);
  } else {
    setModalContent({
      title: "Can't Start Event",
      message: "You haven't joined this event!",
      onConfirm: () => setModalOpen(false),
    });
    setModalOpen(true);
  }
};

  const handleJoinEvent = () => {
    fetch('/server/crazy-cup-server/joinEvent.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventId, username: user.username }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setModalContent({
            title: 'Success',
            message: `You have successfully joined ${event?.cup_name}!`,
            onConfirm: () => setModalOpen(false),
          });
          setModalOpen(true);
          setIsPlayer(true);
          setEvent((prevEvent) => ({
            ...prevEvent,
            players: [...prevEvent.players, user.username],
          }));
        } else {
          setModalContent({
            title: 'Error',
            message: `Error joining event: ${data.message}`,
            onConfirm: () => setModalOpen(false),
          });
          setModalOpen(true);
        }
      })
      .catch((error) => console.error('Error joining event:', error));
  };

  const handleDeleteEvent = () => {
    setModalContent({
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this event?',
      onConfirm: () => {
        fetch(`/server/crazy-cup-server/deleteEvent.php?id=${eventId}`, {
          method: 'DELETE',
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.success) {
              setModalContent({
                title: 'Success',
                message: 'Event deleted successfully!',
                onConfirm: () => navigate('/current-events'),
              });
              setModalOpen(true);
            } else {
              setModalContent({
                title: 'Error',
                message: `Error deleting event: ${data.message}`,
                onConfirm: () => setModalOpen(false),
              });
              setModalOpen(true);
            }
          })
          .catch((error) => console.error('Error deleting event:', error));
      },
    });
    setModalOpen(true);
  };

  const formattedDate = event
    ? new Date(event.date).toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    : '';
  const daySuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };
  const day = event ? new Date(event.date).getDate() : '';
  const formattedDateWithSuffix = event
    ? formattedDate.replace(day, `${day}${daySuffix(day)}`)
    : '';

  return (
    <div id="event-details-page" className="page">
      <h1 className='page-title'>EVENT<br></br>DETAILS</h1>
      {loading ? (
        <EventDetailsSkeletonLoader />
      ) : event ? (
        <>
          <div className="event-details-box event-details">
            <h2></h2>
            <h2>{event.cup_name.toUpperCase()}</h2>
            <p>
              <FontAwesomeIcon icon={faCalendarAlt} className="fa-icon" /> {formattedDateWithSuffix}
            </p>
            <p>
              <FontAwesomeIcon icon={faFlag} className="fa-icon" /> Total Par: {event.total_par}
            </p>
            <h3>Courses</h3>
            <ul id="event-course-list">
              {event.courses.map((course, index) => (
                <li key={index}>
                  {course.name} (Par:&nbsp;{course.par})
                </li>
              ))}
            </ul>
            <h3>Players</h3>
            {event.players.length > 0 ? (
              <ul id="event-player-list">
                {event.players.map((player, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={faUserFriends} className="fa-icon" /> {player.toUpperCase()}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No players have joined this event.</p>
            )}
          </div>

          <button id="start-event" className="edmain-button" onClick={handleStartEvent}>
            Start Event
          </button>

          {!isPlayer && (
            <button id="join-event" className="edmain-button" onClick={handleJoinEvent}>
              Join Event
            </button>
          )}

          <button id="delete-event" className="edmain-button danger" onClick={handleDeleteEvent}>
            Delete Event
          </button>

          <button id="back-to-events" className="edmain-button" onClick={() => navigate('/current-events')}>
            Back to Events
          </button>
        </>
      ) : (
        <div>Error loading event details.</div>
      )}

      {/* Custom Modal */}
      <CustomModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
        onConfirm={modalContent.onConfirm}
      />
    </div>
  );
}

export default EventDetails;